import { DateRangePickerProps } from '@amzn/awsui-components-console/date-range-picker';
import {
  HOURS_PER_DAY,
  MILLIS_PER_MINUTE,
  MINUTES_PER_HOUR,
} from 'src/common/constants';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

/** Placeholder for a date or duration that does not exist. Resolves to 'NA', not an em dash: the
 * absence is a definite outcome (a cancelled or failed job has no delivery date), where an em dash
 * reads as empty/unknown. The em dash stays unlocalized typography elsewhere. */
export const NOT_AVAILABLE_ID = 'value-not-available';

/** A NAMED month, because "02/08/2026" reads as 8 February to a US reader and 2 August to a UK
 * one, and LangHub has both. */
const DAY_MONTH_YEAR_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
};

/** Time and zone included so version-history rows created on the same day stay distinguishable. */
const DAY_MONTH_YEAR_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  ...DAY_MONTH_YEAR_OPTIONS,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  timeZoneName: 'short',
};

/** Zone pinned, locale not: a change-control window end is a cross-region deadline operators quote
 * to each other, so every surface must show the one instant the freeze lifts. Field order and
 * month name are presentation and still follow the reader. */
const DAY_MONTH_YEAR_TIME_UTC_OPTIONS: Intl.DateTimeFormatOptions = {
  ...DAY_MONTH_YEAR_TIME_OPTIONS,
  timeZone: 'UTC',
};

/** English variants are pinned to `en-GB` so all nine render one field order; LangHub's English
 * readers span nine markets and day-month-year suits most. Other locales follow their own. */
const resolveDateLocale = (locale: string): string =>
  locale.split('-')[0].toLowerCase() === 'en' ? 'en-GB' : locale;

/** Constructing an `Intl.DateTimeFormat` is the expensive part and these run per table cell, so
 * they are memoized per (format, locale) instead of the module-scope singletons a fixed locale
 * allowed. Unbounded is fine: the key space is 40 locales times three formats. */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

const dateFormatter = (
  formatKey: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat => {
  const resolved = resolveDateLocale(locale);
  const cacheKey = `${formatKey}|${resolved}`;
  const cached = formatterCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const created = new Intl.DateTimeFormat(resolved, options);
  formatterCache.set(cacheKey, created);
  return created;
};

/** "02 Aug 2026" in the reader's locale and the local timezone. */
export const formatDayMonthYear = (locale: string, date: Date): string =>
  dateFormatter('dmy', locale, DAY_MONTH_YEAR_OPTIONS).format(date);

/** "02 Aug 2026, 14:30:05 GMT+5:30" in the reader's locale and the local timezone. */
export const formatDayMonthYearTime = (locale: string, date: Date): string =>
  dateFormatter('dmyt', locale, DAY_MONTH_YEAR_TIME_OPTIONS).format(date);

/** "08 Oct 2026, 04:00:00 UTC" — the UTC counterpart of {@link formatDayMonthYearTime}, used for
 * change-control window ends (banner, submit modal, edit notice). */
export const formatDayMonthYearTimeUtc = (locale: string, date: Date): string =>
  dateFormatter('dmytUtc', locale, DAY_MONTH_YEAR_TIME_UTC_OPTIONS).format(
    date,
  );

/**
 * Format an epoch-milliseconds timestamp (as string or number) to
 * "DD Mon YYYY", or the not-available placeholder when there is no timestamp.
 * All LangHub API timestamps are epoch milliseconds.
 */
export const formatEpochMillisDate = (
  t: TranslateFn,
  locale: string,
  epochMillis: string | number | null | undefined,
): string => {
  if (!epochMillis) return t(NOT_AVAILABLE_ID);
  return formatDayMonthYear(locale, new Date(Number(epochMillis)));
};

/**
 * Turnaround between two epoch-millisecond timestamps, or the not-available
 * placeholder without a completion timestamp. Rolls into days at 24h ("20d 0h
 * 00m", not an unreadable "480h 00m"); below that, "3h 05m". Clamped at zero so
 * upstream clock skew does not render a negative duration. The d/h/m suffixes are
 * deliberately not localized: a translated unit would not fit the column.
 */
export const computeTurnaround = (
  t: TranslateFn,
  sent: string | number,
  completed: string | number | null | undefined,
): string => {
  if (!completed) return t(NOT_AVAILABLE_ID);
  const elapsedMillis = Math.max(0, Number(completed) - Number(sent));
  const totalMinutes = Math.floor(elapsedMillis / MILLIS_PER_MINUTE);
  const totalHours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = String(totalMinutes % MINUTES_PER_HOUR).padStart(2, '0');
  const days = Math.floor(totalHours / HOURS_PER_DAY);
  if (days === 0) return `${totalHours}h ${minutes}m`;
  return `${days}d ${totalHours % HOURS_PER_DAY}h ${minutes}m`;
};

/** A code rather than a message, so the predicate stays copy-free: `features/track/urlState.ts`
 * validates a range off a shared link and only cares whether it passed, so depending on a
 * `TranslateFn` would make a URL codec carry a localization dependency. */
export type DateRangeValidationCode =
  | 'MISSING_BOUND'
  | 'END_BEFORE_START'
  | 'START_TOO_OLD'
  | 'END_IN_FUTURE'
  | 'RELATIVE_TOO_LONG';

const RANGE_VALIDATION_MESSAGE_IDS: Record<DateRangeValidationCode, string> = {
  MISSING_BOUND: 'date-validation-both-dates',
  END_BEFORE_START: 'date-validation-end-after-start',
  START_TOO_OLD: 'date-validation-start-too-old',
  END_IN_FUTURE: 'date-validation-end-future',
  RELATIVE_TOO_LONG: 'date-validation-relative-too-long',
};

/** Each value is an ICU plural, so the locale applies its own rules rather than the caller
 * appending a literal "s". Shared because the Track picker and the suggested saved-filter name
 * render the same phrase. */
export const RELATIVE_RANGE_MESSAGE_IDS: Record<
  DateRangePickerProps.TimeUnit,
  string
> = {
  second: 'date-range-relative-second',
  minute: 'date-range-relative-minute',
  hour: 'date-range-relative-hour',
  day: 'date-range-relative-day',
  week: 'date-range-relative-week',
  month: 'date-range-relative-month',
  year: 'date-range-relative-year',
};

/** Validates that the selected range is within the last year and not in the future. */
export const dateRangeValidationCode = (
  range: DateRangePickerProps.Value | null,
): DateRangeValidationCode | null => {
  if (range === null) return null;

  const now = new Date();
  now.setHours(23, 59, 59, 999); // end of today — allows today's date to pass

  const oneYearAgo = new Date();
  oneYearAgo.setHours(0, 0, 0, 0); // start of today, then shift back 1 year
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  if (range.type === 'absolute') {
    if (!range.startDate || !range.endDate) return 'MISSING_BOUND';
    if (range.startDate > range.endDate) return 'END_BEFORE_START';
    if (new Date(range.startDate) < oneYearAgo) return 'START_TOO_OLD';
    if (new Date(range.endDate) > now) return 'END_IN_FUTURE';
  }

  if (range.type === 'relative') {
    const totalDays =
      range.unit === 'day'
        ? range.amount
        : range.unit === 'week'
          ? range.amount * 7
          : range.unit === 'month'
            ? range.amount * 30
            : range.amount * 365;
    if (totalDays > 365) return 'RELATIVE_TOO_LONG';
  }

  return null;
};

/** {@link dateRangeValidationCode} in the shape Cloudscape's DateRangePicker expects. */
export const isValidRange = (
  t: TranslateFn,
  range: DateRangePickerProps.Value | null,
): DateRangePickerProps.ValidationResult => {
  const code = dateRangeValidationCode(range);
  return code === null
    ? { valid: true }
    : { valid: false, errorMessage: t(RANGE_VALIDATION_MESSAGE_IDS[code]) };
};

/** Returns a reason string if date is disabled, empty string otherwise. */
export const dateDisabledReason = (t: TranslateFn, date: Date): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const oneYearAgo = new Date(today);
  oneYearAgo.setFullYear(today.getFullYear() - 1);

  if (date > today) return t('date-disabled-future');
  if (date < oneYearAgo) return t('date-disabled-too-old');
  return '';
};

/** Returns true if the date is selectable (within last year, not future). */
export const isDateEnabled = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const oneYearAgo = new Date(today);
  oneYearAgo.setFullYear(today.getFullYear() - 1);
  return date >= oneYearAgo && date <= today;
};
